import { DEFAULT_PAGES } from "@/lib/pages/defaults";
import { DEFAULT_FONT, THEME_COLORS, type ThemeColorKey } from "@/lib/branding/theme";
import { DEFAULT_THEME_NAME } from "@/lib/sportsbook/theme";
import { FOOTER_COLUMNS, SOCIAL_LINKS } from "@/lib/site";
import type { ContentOverrides } from "./store";
import type { SiteContent } from "./types";
import { DEFAULT_NAV_GROUPS, upgradeNav } from "./nav";

const RAIL_SOCIALS = ["X", "Discord", "Telegram"];

const BLANK_COLORS = Object.fromEntries(THEME_COLORS.map((color) => [color.key, ""])) as Record<
  ThemeColorKey,
  string
>;

const BRANDING: SiteContent["branding"] = {
  logoUrl: "",
  logoStyle: "Icon",
  wordmark: true,
  demoBadge: false,
  template: "northstar",
  siteName: "",
  faviconUrl: "",
  metaDescription: "Crypto and fiat casino. Withdrawals in under a minute, cashback with no wagering, and every payout written on-chain.",
  /* WAGMI Casino's licence, read from the production site's own seal embed —
     the same way the Smartico defaults came from the whitelabel's records. A
     different operator overrides it at /admin -> Branding, or clears it to get
     the plain fairness shield back. */
  anjouanLicense: "0aa073a1-8bf0-4bb3-8a7a-862471bf351b",
  /* `{site}` is the platform's name for the site — see footer.ts. A real
     licence line (legal entity, registration, licence number) is the
     operator's to write at /admin -> Branding; the API does not carry one. */
  footerLegal:
    "{site} is an adults-only gambling site. It is not available in every country, and the rules that apply to your location can hide some games or block play entirely. Gambling should stay entertainment: if it stops being that, our responsible gaming page lists free and confidential help that is independent of us.",
};

const THEME: SiteContent["theme"] = {
  ...BLANK_COLORS,
  fontBody: DEFAULT_FONT,
  fontHeading: "",
  radius: "Default",
  buttonFill: "Solid",
  headingCase: "Normal",
  buttonShape: "Default",
  backdropUrl: "",
  motion: "None",
  buttonType: "Body",
  surfaces: "Solid",
  customFonts: [],
};

export const DEFAULT_CONTENT: SiteContent = {
  branding: BRANDING,
  pages: DEFAULT_PAGES,
  localized: {},
  theme: THEME,
  /* English only until the operator enables more at Admin → Languages, so an
     unconfigured deployment shows no selector and no behaviour change. */
  i18n: {
    sv: false,
    de: false,
    es: false,
    pt: false,
    enUS: false,
    esMX: false,
    ptBR: false,
    selectorDisplay: "Flag",
    defaultLocale: "",
    en: true,
    custom: [],
    overrides: [],
  },
  /* The platform's cold wallets, from the whitelabel's own constants
     (COLD_WALLETS, keyed there by token contract): Etherlink USDSM and
     USDC. Matched here by symbol; an operator edits them at
     /admin -> Bankroll. */
  /* The kit's profile images (modal-profile-image) and a set of profile
     backgrounds, managed at /admin -> Profile images. */
  profile: {
    avatars: [
      { name: "Warrior", imageUrl: "/profile/avatars/warrior.webp", locked: false },
      { name: "Chadman", imageUrl: "/profile/avatars/chadman.webp", locked: false },
      { name: "Slickman", imageUrl: "/profile/avatars/slickman.webp", locked: true },
      { name: "Godman", imageUrl: "/profile/avatars/godman.webp", locked: false },
      { name: "Rainbowman", imageUrl: "/profile/avatars/rainbowman.webp", locked: false },
      { name: "Batman", imageUrl: "/profile/avatars/batman.webp", locked: false },
      { name: "Streetman", imageUrl: "/profile/avatars/streetman.webp", locked: false },
      { name: "Plantman", imageUrl: "/profile/avatars/plantman.webp", locked: false },
      { name: "Boomboxman", imageUrl: "/profile/avatars/boomboxman.webp", locked: false },
      { name: "Hairman", imageUrl: "/profile/avatars/hairman.webp", locked: false },
      { name: "Demonman", imageUrl: "/profile/avatars/demonman.webp", locked: false },
      { name: "Afroman", imageUrl: "/profile/avatars/afroman.webp", locked: false },
      { name: "Flowergirl", imageUrl: "/profile/avatars/flowergirl.webp", locked: false },
      { name: "Alien", imageUrl: "/profile/avatars/alien.webp", locked: false },
      { name: "Musicman", imageUrl: "/profile/avatars/musicman.webp", locked: false },
      { name: "Eggman", imageUrl: "/profile/avatars/eggman.webp", locked: false },
      { name: "Travelman", imageUrl: "/profile/avatars/travelman.webp", locked: false },
      { name: "Hippiewoman", imageUrl: "/profile/avatars/hippiewoman.webp", locked: false },
      { name: "Chillwoman", imageUrl: "/profile/avatars/chillwoman.webp", locked: false },
      { name: "Frogman", imageUrl: "/profile/avatars/frogman.webp", locked: false },
      { name: "Portrait", imageUrl: "/profile/avatars/portrait.webp", locked: false },
    ],
    backgrounds: [
      { name: "Violet Aurora", imageUrl: "/profile/backgrounds/violet-aurora.svg", locked: false },
      { name: "Midnight Blue", imageUrl: "/profile/backgrounds/midnight-blue.svg", locked: false },
      { name: "Emerald Rush", imageUrl: "/profile/backgrounds/emerald-rush.svg", locked: false },
      { name: "Ember Glow", imageUrl: "/profile/backgrounds/ember-glow.svg", locked: false },
      { name: "Neon Magenta", imageUrl: "/profile/backgrounds/neon-magenta.svg", locked: false },
      { name: "Arctic Steel", imageUrl: "/profile/backgrounds/arctic-steel.svg", locked: false },
    ],
  },
  bankroll: {
    coldWallets: [
      { symbol: "USDSM", address: "0x95af98c907611fCAf1Ac1A3d965AC2DCcDd872c3" },
      { symbol: "USDC", address: "0xa05C5C2f2E3b4b9476e96dD321E5aC3Ef2e6429B" },
    ],
  },
  /* Open to every signed-in player until the operator raises the bar at
     /admin -> Chat. */
  chat: {
    minTierOrdinal: 0,
    winMultiplier: 10,
    winsPerMinute: 1,
  },
  seo: {
    metaTitle: "",
    keywords: [],
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
    twitterCard: "summary_large_image",
    twitterSite: "",
    twitterCreator: "",
  },
  flags: {
    navLayout: "Sidebar",
    navGroupStyle: "Grouped",
    collapsedRail: true,
    categoryStyle: "Pills",
    topSearch: false,
    registerButton: false,
    headerStyle: "Solid",
    pageWidth: "Full",
    headerActions: "Right",
    signInStyle: "Outline",
    chatSide: "Right",
    chatPreview: false,
    heroEnabled: true,
    liveFeedsEnabled: true,
    promoCarouselEnabled: true,
    bankrollEnabled: true,
    chatEnabled: true,
    sportsEnabled: true,
    walletEnabled: true,
    favoritesEnabled: true,
    xpWidgetEnabled: true,
    missionWidgetEnabled: true,
    notificationsEnabled: true,
    twoFactorEnabled: false,
    seoIndexing: false,
  },
  nav: {
    primary: [
      { label: "Home", href: "/", icon: "home" },
      { label: "Favorites", href: "/c/favorites", icon: "star" },
      { label: "Recently Played", href: "/c/recent", icon: "history" },
      { label: "New Releases", href: "/c/new-releases", icon: "sparkles" },
    ],
    groups: DEFAULT_NAV_GROUPS.map((group) => ({ ...group, items: group.items.map((item) => ({ ...item })) })),
    tail: [
      { label: "Missions", href: "/missions", icon: "crossed" },
      { label: "Rewards", href: "/rewards", icon: "trophy", badge: "NEW" },
      { label: "Blog", href: "/blog/why-flipcoin", icon: "book" },
    ],
    mobile: [
      { label: "Menu", icon: "menu", href: "", prefixes: [], exact: [] },
      { label: "Casino", icon: "spade", href: "/casino", prefixes: ["/casino", "/c/", "/game/"], exact: ["/"] },
      { label: "Sports", icon: "target", href: "/sports", prefixes: ["/sports"], exact: [] },
      { label: "Rewards", icon: "gift", href: "/rewards", prefixes: ["/rewards"], exact: [] },
      { label: "Support", icon: "help", href: "/support/help-center", prefixes: ["/support"], exact: [] },
      { label: "Profile", icon: "user", href: "/account", prefixes: ["/account"], exact: [], avatar: true },
    ],
  },
  footer: {
    columns: FOOTER_COLUMNS.map((c) => ({
      title: c.title,
      links: c.links.map((l) => ({ label: l.label, href: l.href, external: Boolean(l.external) })),
    })),
  },
  socials: SOCIAL_LINKS.map((s) => ({ ...s, rail: RAIL_SOCIALS.includes(s.label) })),
  missions: [
    { title: "Wager 250", metric: "wagered", target: 250 },
    { title: "Place 50 bets", metric: "bets", target: 50 },
    { title: "Earn 100 in rewards", metric: "rewards", target: 100 },
  ],
  achievements: [
    { title: "First Blood", body: "Place your first bet.", metric: "bets", target: 1, icon: "target" },
    { title: "Century", body: "Place 100 bets.", metric: "bets", target: 100, icon: "grid" },
    { title: "High Roller", body: "Wager 1,000,000 lifetime.", metric: "wagered", target: 1_000_000, icon: "coins" },
    { title: "Big Swing", body: "Wager 10,000 lifetime.", metric: "wagered", target: 10_000, icon: "bolt" },
    { title: "Regular", body: "Make 25 transactions.", metric: "transactions", target: 25, icon: "clock" },
    { title: "Completionist", body: "Earn 500 in rewards.", metric: "rewards", target: 500, icon: "check-circle" },
  ],
  notifications: [
    {
      title: "Weekly race is live",
      body: "Standings reset Sunday at midnight. Every settled bet counts.",
      icon: "trophy",
      href: "/leaderboards",
      unread: true,
    },
    {
      title: "Verification reminder",
      body: "Upload a proof of address to lift withdrawal limits.",
      icon: "check-circle",
      href: "/account",
      unread: false,
    },
  ],
  sportsbook: {
    /* The BetBy theme the book is built from. It is still theirs — `themed`
       is what repaints it in Northstar's colours on the way through. */
    themeName: DEFAULT_THEME_NAME,
    themed: true,
    lang: "en",
    oddsFormat: "DECIMAL",
  },
  /* Off until an operator switches it on at /admin -> Smartico, which is the
     whitelabel's default too: a site record there carries `smartico: null`
     until someone fills it in, and most brands never do.

     This used to ship the whitelabel's development label — the public label
     and brand keys every dev site in that table carries, not credentials —
     on the theory that the origin would not be allowlisted and the widgets
     would stay on their fallbacks. It was, and they did not: every brand the
     factory stamped out came up drawing Smartico levels and missions for a
     programme it does not run, and the only way off was to blank the keys by
     hand. The keys are in docs/findings/can-northstar-use-flipcoins-smartico.md
     for a deployment that wants them. */
  smartico: {
    scriptUrl: "",
    labelKey: "",
    brandKey: "",
    /* Which ladder the level widget draws once Smartico is configured. The
       whitelabel's own default is Smartico, so this matches it; with the keys
       blank it makes no difference, since the widget draws the platform's tiers
       whenever Smartico cannot answer. */
    levelSource: "Smartico",
  },
};

function isSection(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function mergeContent(overrides: ContentOverrides): SiteContent {
  const merged = { ...DEFAULT_CONTENT } as Record<string, unknown>;
  for (const key of Object.keys(overrides) as (keyof SiteContent)[]) {
    /* A nav stored before the sidebar groups were a list is read in the
       current shape, so its edited links land in the groups. */
    const override = key === "nav" ? upgradeNav(overrides.nav) : overrides[key];
    if (override === undefined) continue;
    const base = DEFAULT_CONTENT[key];
    merged[key] = isSection(base) && isSection(override) ? { ...base, ...override } : override;
  }
  return merged as SiteContent;
}
